import React, { useState } from "react";
import { motion } from "framer-motion";
import serviceImage from "../assets/images/service image.avif";
import ServiceScroll from "../components/common/Servicescroll";
import { useNavigate } from "react-router-dom";
import ScrollingText from "../components/common/ScrollingText";
import {
  webDevelopmentIcon,
  uiUxIcon, mobileDevelopmentIcon,
  eCommerceIcon, portfolioIcon,
  resumeIcon, fullStackIcon,
  pythonIcon, careerIcon,
  searchIcon, planningIcon,
  designDevelopmentIcon, testingQualityIcon,
  deploymentIcon, maintenanceSupportIcon
} from "../components/svg/service";



const services = [

  {
    title: "Web Development",
    description: "Custom websites, web apps, and portals tailored to your business goals using modern tech stacks.",
    icon: webDevelopmentIcon,
    details: "Full-stack development with React, Node.js, and modern frameworks",
    features: ["Responsive Design", "SEO Optimization", "Performance Tuning", "Security Implementation"]
  },
  {
    title: "UI/UX Design",
    description: "User-focused designs that blend aesthetics with usability for a seamless digital experience.",
    icon: uiUxIcon,
    details: "Wireframing, prototyping, and user testing for optimal experiences",
    features: ["User Research", "Wireframing", "Interactive Prototypes", "Design Systems"]
  },
  {
    title: "Mobile App Development",
    description: "Cross-platform mobile apps that deliver performance and scalability on Android and iOS.",
    icon: mobileDevelopmentIcon,
    details: "React Native development for cost-effective cross-platform solutions",
    features: ["Cross-Platform", "Native Performance", "App Store Deployment", "Push Notifications"]
  },
  {
    title: "E-commerce Solutions",
    description: "Robust e-commerce platforms with payment integration, inventory, and analytics.",
    icon: eCommerceIcon,
    details: "Shopify, WooCommerce, and custom e-commerce development",
    features: ["Payment Integration", "Inventory Management", "Sales Analytics", "Customer Accounts"]
  },
  {
    title: "Portfolio Development",
    description: "Stunning portfolio websites that showcase your work and attract potential clients.",
    icon: portfolioIcon,
    details: "Responsive designs with CMS integration for easy content management",
    features: ["Custom Design", "CMS Integration", "Gallery Features", "Contact Systems"]
  },
  {
    title: "ATS-Friendly Resumes",
    description: "Professionally crafted resumes optimized for Applicant Tracking Systems.",
    icon: resumeIcon,
    details: "Industry-specific templates with keyword optimization",
    features: ["ATS Optimization", "Industry Templates", "Cover Letters", "LinkedIn Profile Makeovers"]
  },
  {
    title: "Full-Stack Courses",
    description: "Comprehensive training programs covering frontend and backend technologies.",
    icon: fullStackIcon,
    details: "MERN stack, Python Django, and project-based learning approach",
    features: ["Project-Based Learning", "Mentorship", "Code Reviews", "Career Guidance"]
  },
  {
    title: "Python Programming",
    description: "Beginner to advanced Python courses with practical applications.",
    icon: pythonIcon,
    details: "Data analysis, automation, web development with Python",
    features: ["Beginner to Advanced", "Real-world Projects", "Data Science Fundamentals", "Automation Scripting"]
  },
  {
    title: "Career Support",
    description: "Job search assistance, interview preparation, and career guidance.",
    icon: careerIcon,
    details: "Mock interviews, LinkedIn optimization, and job placement support",
    features: ["Mock Interviews", "Resume Reviews", "Job Search Strategy", "Negotiation Coaching"]
  },
];

const serviceStats = [
  { number: "10+", label: "Projects Completed" },
  { number: "98%", label: "Client Satisfaction" },
  { number: "30+", label: "Technologies Used" },
  { number: "24/7", label: "Support Available" }
];

const processSteps = [
  {
    title: "Discovery & Consultation",
    description: "We begin by understanding your goals, target audience, and project requirements through detailed discussions.",
    icon: searchIcon
  },
  {
    title: "Planning & Strategy",
    description: "Our team creates a comprehensive project plan with timelines, milestones, and technology stack recommendations.",
    icon: planningIcon
  },
  {
    title: "Design & Development",
    description: "We bring your vision to life with iterative design and development cycles, keeping you involved at every step.",
    icon: designDevelopmentIcon
  },
  {
    title: "Testing & Quality Assurance",
    description: "Rigorous testing ensures your solution works flawlessly across devices and meets all requirements.",
    icon: testingQualityIcon
  },
  {
    title: "Deployment & Launch",
    description: "We handle the complete deployment process and ensure a smooth transition to live environment.",
    icon: deploymentIcon
  },
  {
    title: "Maintenance & Support",
    description: "Our relationship continues with ongoing support, updates, and optimization services.",
    icon: maintenanceSupportIcon
  }
];

const testimonials = [
  {
    id: 1,
    name: "Sarah Johnson",
    role: "Freelance Designer",
    content: "The portfolio they created for me helped me land three new clients in the first month! The attention to detail and understanding of my needs was exceptional.",
    avatar: "👩",
    rating: 5
  },
  {
    id: 2,
    name: "Michael Chen",
    role: "Career Changer",
    content: "Their Python course gave me the skills I needed to transition into tech. The instructors are amazing! The project-based approach made learning engaging and practical.",
    avatar: "👨",
    rating: 5
  },
  {
    id: 3,
    name: "Emma Rodriguez",
    role: "Small Business Owner",
    content: "Our e-commerce site has seen a 40% increase in sales since they redesigned it. The checkout process is now seamless, and mobile shopping works perfectly.",
    avatar: "👩",
    rating: 5
  },
  {
    id: 4,
    name: "David Wilson",
    role: "Job Seeker",
    content: "The ATS-friendly resume service helped me get interviews at companies that previously ignored my applications. I landed my dream job in just 3 weeks!",
    avatar: "👨",
    rating: 5
  },
  {
    id: 5,
    name: "Priya Sharma",
    role: "Startup Founder",
    content: "Their web development service delivered exactly what we needed to launch our MVP. The team was responsive, professional, and delivered ahead of schedule.",
    avatar: "👩",
    rating: 5
  }
];

const faqs = [
  {
    question: "How long does a typical web development project take?",
    answer: "The timeline varies based on project complexity. A simple website may take 2-4 weeks, while a complex web application can take 3-6 months. We provide a detailed timeline during our initial consultation."
  },
  {
    question: "Do you offer ongoing maintenance after project completion?",
    answer: "Yes, we offer comprehensive maintenance plans that include regular updates, security monitoring, performance optimization, and technical support. Our clients can choose from monthly or annual plans based on their needs."
  },
  {
    question: "Can you work with our existing design or development team?",
    answer: "Absolutely! We frequently collaborate with in-house teams, providing specialized expertise where needed. We're flexible and can adapt to your existing workflows and processes."
  },
  {
    question: "What makes your ATS-friendly resumes different?",
    answer: "Our resumes are crafted by professionals who understand how Applicant Tracking Systems work. We optimize for keywords, formatting, and content structure that passes A screening while maintaining human readability and appeal."
  },
  {
    question: "Do you provide source code and project documentation?",
    answer: "Yes, upon project completion, we deliver all source code, assets, and comprehensive documentation. You maintain full ownership of all intellectual property created during the project."
  }
];



// Custom Arrow Components
const ChevronLeftIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      when: "beforeChildren"
    }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 10
    }
  }
};

const headerVariants = {
  hidden: { y: -20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 10,
      delay: 0.2
    }
  }
};

const heroVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.8,
      ease: "easeOut"
    }
  }
};

const Service = () => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [expandedService, setExpandedService] = useState(null);
  const [expandedFaq, setExpandedFaq] = useState(null);

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) =>
      prev === testimonials.length - 1 ? 0 : prev + 1
    );
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) =>
      prev === 0 ? testimonials.length - 1 : prev - 1
    );
  };

  const toggleService = (index) => {
    setExpandedService(expandedService === index ? null : index);
  };

  const toggleFaq = (index) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };
  const navigate = useNavigate();
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };
  return (
    <div className="min-h-screen bg-gray-50">

      {/* 4K Hero Image Section */}
      <motion.div
        initial="hidden"
        animate="visible"
        variants={heroVariants}
        className="relative w-full h-screen max-h-[80vh] overflow-hidden"
      >
        <img
          src={serviceImage}
          alt="Technology Services"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="text-center px-4"
          >
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-6">
              Premium Digital Solutions
            </h1>
            <p className="text-xl text-white max-w-3xl mx-auto">
              From concept to deployment, we deliver exceptional digital experiences that drive results and exceed expectations.
            </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => scrollToSection("ourservice")}
              className="mt-8 bg-yellow-400 text-gray-900 font-semibold px-8 py-3 rounded-lg shadow-md hover:bg-yellow-500 transition duration-300"
            >
              Explore Our Services
            </motion.button>
          </motion.div>
        </div>
      </motion.div>

      {/* Stats Section */}
      <motion.section
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        viewport={{ once: true }}
        className="py-16 bg-white"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {serviceStats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <h3 className="text-4xl font-bold text-yellow-500 mb-2">{stat.number}</h3>
                <p className="text-gray-600">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>


      <ScrollingText />
      <ServiceScroll />

      {/* Services Section */}
      <motion.section
        initial="hidden"
        whileInView="visible"
        variants={containerVariants}
        viewport={{ once: true }}
        className="w-full py-16 bg-gray-50"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Animated header section */}
          <motion.div
            variants={headerVariants}
            className="text-center w-full mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Our Comprehensive Services
            </h2>
            <div className="h-1 w-20 bg-yellow-400 mx-auto mb-6"></div>
            <motion.p
              variants={itemVariants}
              className="text-gray-600 text-lg max-w-4xl mx-auto"
            >
              We deliver full-service solutions tailored to your business, combining creativity,
              strategy, and technology at competitive prices. Each service is designed to address
              specific challenges and deliver measurable results.
            </motion.p>
          </motion.div>

          {/* Animated services grid */}
          <motion.div
            variants={containerVariants}
            className="w-full grid gap-6 sm:grid-cols-2 lg:grid-cols-3"
          >
            {services.map((service, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                whileHover={{
                  y: -5,
                  transition: { duration: 0.2 }
                }}
                className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition duration-300 border-t-4 border-yellow-400"
              >
                <motion.div
                  animate={{ rotate: [0, 10, -10, 0] }}
                  transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                  className="text-4xl mb-4"
                >
                  {service.icon}
                </motion.div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">
                  {service.title}
                </h3>
                <p className="text-gray-600 mb-3">{service.description}</p>
                <p className="text-sm text-gray-500 mb-4">{service.details}</p>

                <button
                  onClick={() => toggleService(index)}
                  className="text-yellow-600 font-medium flex items-center"
                >
                  {expandedService === index ? 'Show Less' : 'See Features'}
                  <svg xmlns="http://www.w3.org/2000/svg" className={`h-5 w-5 ml-1 transition-transform ${expandedService === index ? 'rotate-180' : ''}`} viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>

                {expandedService === index && (
                  <motion.ul
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="mt-4 pl-5 space-y-2"
                  >
                    {service.features.map((feature, i) => (
                      <li key={i} className="text-gray-600 list-disc">{feature}</li>
                    ))}
                  </motion.ul>
                )}
              </motion.div>
            ))}
          </motion.div>
        </div>
      </motion.section>

      {/* Process Section */}
      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.7 }}
        viewport={{ once: true }}
        className="py-16 bg-white"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Process</h2>
            <div className="h-1 w-20 bg-yellow-400 mx-auto mb-6"></div>
            <p className="text-gray-600 max-w-3xl mx-auto text-lg">
              We follow a structured approach to ensure your project's success from conception to launch and beyond.
            </p>
          </div>

          <div className="relative overflow-x-hidden">
            {/* Timeline line */}
            <div className="absolute left-6 md:left-1/2 top-0 h-full w-1 bg-yellow-200 transform md:-translate-x-1/2"></div>

            {/* Process steps */}
            <div className="space-y-12">
              {processSteps.map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.2 }}
                  viewport={{ once: true }}
                  className={`relative flex flex-col md:flex-row ${index % 2 === 0 ? 'md:flex-row-reverse' : ''}`}
                >
                  {/* Spacer for desktop layout */}
                  <div className="md:w-1/2 mb-4 md:mb-0"></div>

                  {/* Number circle */}
                  <div className="md:w-1/2 flex md:justify-center px-2 absolute md:relative left-0 md:left-auto -top-2 md:top-auto">
                    <div className="w-10 h-10 rounded-full bg-yellow-400 mb-2 flex items-center justify-center text-white font-bold z-10">
                      {index + 1}
                    </div>
                  </div>

                  {/* Content card */}
                  <div className={`md:w-1/2 ${index % 2 === 0 ? 'md:pr-8 md:text-right' : 'md:pl-8'} mt-8 md:mt-0 ml-12 md:ml-0`}>
                    <div className="bg-gray-50 p-6 rounded-lg shadow-sm mx-2 md:mx-0">
                      <div className="text-3xl mb-2">{step.icon}</div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-2">{step.title}</h3>
                      <p className="text-gray-600">{step.description}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </motion.section>

      {/* Testimonials Carousel */}
      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.7 }}
        viewport={{ once: true }}
        className="py-16 bg-gray-900 text-white"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Client Success Stories</h2>
            <div className="h-1 w-20 bg-yellow-400 mx-auto mb-6"></div>
          </div>

          <div className="relative max-w-4xl mx-auto">
            <div className="bg-gray-800 p-8 rounded-xl shadow-lg">
              <div className="flex items-start mb-6">
                <div className="text-4xl mr-4">{testimonials[currentTestimonial].avatar}</div>
                <div>
                  <div className="flex mb-1">
                    {[...Array(testimonials[currentTestimonial].rating)].map((_, i) => (
                      <svg key={i} xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                  </div>
                  <h4 className="text-xl font-semibold">{testimonials[currentTestimonial].name}</h4>
                  <p className="text-yellow-400">{testimonials[currentTestimonial].role}</p>
                </div>
              </div>
              <p className="text-lg italic">"{testimonials[currentTestimonial].content}"</p>
            </div>

            <button
              onClick={prevTestimonial}
              className="absolute left-3 md:left-0 md:top-1/2 transform -translate-y-1/2 -translate-x-5 bg-yellow-400 rounded-full p-3 shadow-md hover:bg-yellow-500 transition-colors"
            >
              <ChevronLeftIcon />
            </button>
            <button
              onClick={nextTestimonial}
              className="absolute right-3 md:right-0 md:top-1/2 transform -translate-y-1/2 translate-x-5 bg-yellow-400 rounded-full p-3 shadow-md hover:bg-yellow-500 transition-colors"
            >
              <ChevronRightIcon />
            </button>

            <div className="flex justify-center mt-6 space-x-2">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentTestimonial(index)}
                  className={`h-3 w-3 rounded-full ${currentTestimonial === index ? 'bg-yellow-400' : 'bg-gray-600'}`}
                />
              ))}
            </div>
          </div>
        </div>
      </motion.section>

      {/* FAQ Section */}
      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.7 }}
        viewport={{ once: true }}
        className="py-16 bg-white"
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
            <div className="h-1 w-20 bg-yellow-400 mx-auto mb-6"></div>
            <p className="text-gray-600 max-w-3xl mx-auto text-lg">
              Find answers to common questions about our services, process, and what you can expect when working with us.
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="border-b border-gray-200 pb-4"
              >
                <button
                  onClick={() => toggleFaq(index)}
                  className="flex justify-between items-center w-full text-left font-semibold text-gray-800 py-2 focus:outline-none"
                >
                  <span>{faq.question}</span>
                  <svg xmlns="http://www.w3.org/2000/svg" className={`h-5 w-5 text-yellow-500 transition-transform ${expandedFaq === index ? 'rotate-180' : ''}`} viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
                {expandedFaq === index && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="text-gray-600 mt-2"
                  >
                    {faq.answer}
                  </motion.div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* CTA Section */}
      <motion.section
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        viewport={{ once: true }}
        className="py-16 bg-gradient-to-r from-yellow-400 to-yellow-500"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Ready to Start Your Project?</h2>
          <p className="text-gray-800 max-w-3xl mx-auto mb-8 text-lg">
            Get in touch with us today for a free consultation. Let's discuss how we can help you achieve your goals with our tailored digital solutions.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate("/contact")}
              className="bg-gray-900 text-white font-semibold px-8 py-3 rounded-lg shadow-md hover:bg-gray-800 transition duration-300"
            >
              Get Started Now
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-white text-gray-900 font-semibold px-8 py-3 rounded-lg shadow-md hover:bg-gray-100 transition duration-300"
            >
              View Our Portfolio
            </motion.button>
          </div>
        </div>
      </motion.section>
    </div>
  );
};

export default Service;